import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Chip, Dialog, DialogTitle, DialogContent, IconButton, Menu, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api';

const CorrectionReport = () => {
  // Helper to safely get 1st of the month and Today in local YYYY-MM-DD format
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  
  const defaultStart = `${yyyy}-${mm}-01`;
  const defaultEnd = `${yyyy}-${mm}-${dd}`;

  const [filters, setFilters] = useState({ emp_id: '', start_date: defaultStart, end_date: defaultEnd, reason: '', department_id: '', designation_id: '' });
  const [dropdowns, setDropdowns] = useState({ departments: [], designations: [], reasons: [] });
  
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [columns, setColumns] = useState({ l1: false, l2: false, dept: false, desig: false });
  const [anchorEl, setAnchorEl] = useState(null);

  // Secure Blob Attachment State
  const [popupOpen, setPopupOpen] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState('');

  useEffect(() => {
    api.get('/correction-dashboard/filters-data').then(res => setDropdowns(res.data)).catch(console.error);
  }, []);

  useEffect(() => { fetchReport(); }, [page, rowsPerPage]);

  const fetchReport = async () => {
    const query = new URLSearchParams({ ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')), page, rowsPerPage }).toString();
    try {
      const res = await api.get(`/correction-report/list?${query}`);
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
  };

  const handleExport = () => {
    const query = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v !== '')).toString();
    window.open(`http://${window.location.hostname}:8000/api/v1/correction-report/export?${query}`, '_blank');
  };

  // FIX: Securely fetch blob with auth to prevent blank iframe
  const handleViewAttachment = async (id) => {
    try {
      const res = await api.get(`/correction-report/attachment/${id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      setAttachmentUrl(url);
      setPopupOpen(true);
    } catch (err) {
      console.error("Failed to load attachment", err);
      alert("Failed to securely load the attachment.");
    }
  };

  const closePopup = () => {
    setPopupOpen(false);
    if (attachmentUrl) URL.revokeObjectURL(attachmentUrl); // Prevent memory leaks
  };

  const getDisplayDateText = () => {
    if (filters.start_date && filters.end_date) {
      const start = new Date(filters.start_date);
      const end = new Date(filters.end_date);
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return start.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
      return `${filters.start_date} to ${filters.end_date}`;
    }
    return 'All Time';
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Emp ID" value={filters.emp_id} onChange={e => setFilters({...filters, emp_id: e.target.value})} />
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} />
        <TextField select size="small" label="Reason" sx={{ minWidth: 120 }} value={filters.reason} onChange={e => setFilters({...filters, reason: e.target.value})}>
          <MenuItem value="">All</MenuItem>
          {dropdowns.reasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Department" sx={{ minWidth: 150 }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="">All</MenuItem>
          {dropdowns.departments.map(d => <MenuItem key={d.id} value={d.id}>{d.department_name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Designation" sx={{ minWidth: 150 }} value={filters.designation_id} onChange={e => setFilters({...filters, designation_id: e.target.value})}>
          <MenuItem value="">All</MenuItem>
          {dropdowns.designations.map(d => <MenuItem key={d.id} value={d.id}>{d.designation_name}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={() => { setPage(0); fetchReport(); }} disableElevation>Search</Button>
        <Button variant="outlined" startIcon={<GetAppIcon />} color="success" onClick={handleExport} sx={{ ml: 'auto' }}>Export CSV</Button>
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>Columns</Button>
      </Paper>

      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2, pl: 1 }}>
        Showing data for: <strong style={{ color: '#1976d2' }}>{getDisplayDateText()}</strong>
      </Typography>

      {/* COLUMN TOGGLE MENU */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <FormGroup sx={{ p: 2 }}>
          <FormControlLabel control={<Checkbox checked={columns.l1} onChange={e => setColumns({...columns, l1: e.target.checked})} />} label="L1 Approver" />
          <FormControlLabel control={<Checkbox checked={columns.l2} onChange={e => setColumns({...columns, l2: e.target.checked})} />} label="L2 Approver" />
          <FormControlLabel control={<Checkbox checked={columns.dept} onChange={e => setColumns({...columns, dept: e.target.checked})} />} label="Department" />
          <FormControlLabel control={<Checkbox checked={columns.desig} onChange={e => setColumns({...columns, desig: e.target.checked})} />} label="Designation" />
        </FormGroup>
      </Menu>

      {/* DATA TABLE */}
      <Paper elevation={2}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Emp ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Attachment</TableCell>
              {columns.l1 && <TableCell>L1 Approver</TableCell>}
              {columns.l2 && <TableCell>L2 Approver</TableCell>}
              {columns.dept && <TableCell>Department</TableCell>}
              {columns.desig && <TableCell>Designation</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? <TableRow><TableCell colSpan={10} align="center">No records found.</TableCell></TableRow> : data.map(row => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ fontWeight: 'bold' }}>{row.emp_id}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.status} color={row.status.includes('APPROVED') ? 'success' : row.status.includes('REJECTED') ? 'error' : 'warning'} />
                </TableCell>
                <TableCell>
                  {row.has_attachment ? <Button size="small" variant="outlined" onClick={() => handleViewAttachment(row.id)}>View File</Button> : '--'}
                </TableCell>
                {columns.l1 && <TableCell>{row.l1_approver || '--'}</TableCell>}
                {columns.l2 && <TableCell>{row.l2_approver || '--'}</TableCell>}
                {columns.dept && <TableCell>{row.department_name || '--'}</TableCell>}
                {columns.desig && <TableCell>{row.designation_name || '--'}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </Paper>

      {/* ATTACHMENT POPUP */}
      <Dialog open={popupOpen} onClose={closePopup} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
          Document Viewer
          <Box>
            <Button size="small" variant="contained" startIcon={<DownloadIcon />} component="a" href={attachmentUrl} download="attachment" sx={{ mr: 2 }} disableElevation>
              Download File
            </Button>
            <IconButton onClick={closePopup}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '70vh', p: 0, bgcolor: '#fafafa' }}>
          <iframe src={attachmentUrl} width="100%" height="100%" style={{ border: 'none' }} title="Attachment" />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CorrectionReport;